import type { CreateElement } from 'vue'
import Router, { NavigationGuard, RouteConfig } from 'vue-router'
import { useAuthService } from '@b2ag/auth'
import { useI18nForVue2 } from '@invivodf/locales/src/useI18n'
import VAuthCallback from '@/auth/views/VAuthCallback.vue'
import { getStore } from '@/store'

import VMicroFrontProvider from '@b2ag/micro-front-tools/src/components/VMicroFrontProvider.vue'

import { discountAssociationParametersRoutes, discountRoutes } from '@invivodf/common/src/contexts/discount'
import { logisticRoutes } from '@invivodf/common/src/contexts/logistic-offer/routes/logistic-routes'
import { LogisticRouteName } from '@invivodf/common/src/contexts/logistic-offer/routes/logistic-route-name'
import { BRAND_MANAGE_SCOPES } from '@invivodf/front-seller-auth/src/BRAND_MANAGE_SCOPES'
import { PRODUCT_MANAGER_MANAGE_SCOPES } from '@invivodf/front-seller-auth/src/PRODUCT_MANAGER_MANAGE_SCOPES'
import {
  checkFeature,
  DynamicFeaturesEnum,
  EnvFeaturesEnum,
  FeatureType,
} from '@invivodf/front-seller-feature-flipping'
import VImportReports from '@/contexts/parameters/views/VImportReports/VImportReports.vue'
import VImportReport from '@/contexts/parameters/views/VImportReport/VImportReport.vue'
import CImportStoreCustomer from '@/components/CImportStoreCustomer/CImportStoreCustomer.vue'
import VHome from '@/views/VHome/VHome.vue'
import VLayoutMain from '@/views/VLayoutMain/VLayoutMain.vue'
import VPageNotFound from '@/views/VPageNotFound/VPageNotFound.vue'
import VProductMicro from '@/views/VProductMicro/VProductMicro.vue'
import VParameters from '@/contexts/parameters/views/VParameters/VParameters.vue'
import VParametersList from '@/contexts/parameters/views/VParametersList/VParametersList.vue'
import VVariant from '@/views/VVariant/VVariant.vue'
import VZoneCharges from '@/views/VZoneCharges/VZoneCharges.vue'
import VOffers from '@/views/VOffers/VOffers.vue'
import VQuotas from '@/views/VQuota/VQuota.vue'
import VAuthCallbackError from '@/auth/views/VAuthCallbackError.vue'

import CImportMembers from '@/components/CImportMembers/CImportMembers.vue'
import CImportTechnicalSales from '@/components/CImportTechnicalSales/CImportTechnicalSales.vue'
import CImportStores from '@/components/CImportStores/CImportStores.vue'
import CImportStock from '@/components/CImportStock/CImportStock.vue'
import CThresholdsStock from '@/components/thresholdsStock/CThresholdsStock/CThresholdsStock.vue'
import CImportElectronicInvoicingAddresses from '@/components/CImportElectronicInvoicingAddresses/CImportElectronicInvoicingAddresses.vue'
import CImportShippingAddresses from '@/components/CImportShippingAddresses/CImportShippingAddresses.vue'
import CImportVariants from '@/components/CImportVariants/CImportVariants.vue'
import CImportVariantSegmentations from '@/components/CImportVariantSegmentations/CImportVariantSegmentations.vue'
import CImportQuotas from '@/components/CImportQuotas/CImportQuotas.vue'
import CImportOffers from '@/components/CImportOffers/CImportOffers.vue'
import CImportSegments from '@/components/CImportSegments/CImportSegments.vue'
import CDetailsPaiementsCb from '@/components/CDetailsPaiementsCb/CDetailsPaiementsCb.vue'
import CPaymentDueDatesList from '@/components/CPaymentDueDatesList/CPaymentDueDatesList.vue'

import VMinimalLayout from '@/views/VMinimalLayout/VMinimalLayout.vue'
import CImportPartnerContact from '@/components/CImportPartnerContact/CImportPartnerContact.vue'
import CImportProductsAdditionalData from '@/components/CImportProductsAdditionalData/CImportProductsAdditionalData.vue'
import CImportUsersRoles from '@/components/CImportUsersRoles/CImportUsersRoles.vue'
import CImportSalesStores from '@/components/CImportUsersRoles/CImportSalesStores.vue'

// Stand-in for the wrapper views the catalogue screens used to mount (`VSearch`,
// `VVirtualCategories`, `VCustomFilters`), which rendered a `<router-view>` and nothing else of
// their own once the banner moved into the micro-front. Keeping the nesting keeps the child
// `path` segments — and therefore the route snapshot — byte-identical.
const VRouterViewPassThrough = {
  name: 'VRouterViewPassThrough',
  render: (h: CreateElement) => h('router-view'),
}

function getI18n() {
  const i18n = useI18nForVue2()
  return {
    _: i18n.t.bind(i18n),
  }
}

// B2ag route set — mounted only in the B2ag world. `getRouter` is injected
// lazily so the OAuth callback and micro-front props can reference the router
// instance created in router.ts.
// Same flag as the menu entry: without this guard, the URLs stay reachable on brands where the entry is hidden.
const ordersToValidateFeatureGuard: NavigationGuard = async (_to, _from, next) => {
  const isEnabled = await checkFeature.execute({
    id: EnvFeaturesEnum.ORDERS_TO_VALIDATE_FEATURE,
    type: FeatureType.Env,
  })
  next(isEnabled ? undefined : { name: 'orders' })
}

export function getB2agRoutes(getRouter: () => Router): RouteConfig[] {
  return [
    {
      path: '/oauth',
      component: VMinimalLayout,
      children: [
        {
          path: 'callback',
          name: 'oauth-callback',
          component: VAuthCallback,
          props: () => {
            return {
              targetRoute: '/',
              router: getRouter(),
              auth: useAuthService(),
              i18n: getI18n(),
            }
          },
          beforeEnter(to, _, next) {
            if (to.query.error_description) {
              next({ name: 'auth-callback-error', query: to.query })
            } else {
              next()
            }
          },
        },
        {
          path: 'error',
          name: 'auth-callback-error',
          component: VAuthCallbackError,
          props: (route) => {
            return {
              errorDetails: {
                clientId: route.query?.client_id,
                connection: route.query?.connection,
                lang: route.query?.lang,
                errorCode: route.query?.error_code || route.query?.error,
                tracking: route.query?.tracking,
                errorDescription: route.query?.error_description,
              },
              auth: useAuthService(),
            }
          },
        },
      ],
    },
    {
      path: '/',
      component: VLayoutMain,
      children: [
        {
          path: '',
          redirect: 'accueil',
        },
        {
          path: 'accueil',
          name: 'home',
          component: VHome,
        },
        {
          path: 'produits/:productId',
          name: 'product',
          component: VProductMicro,
        },
        {
          path: 'variant/:productId/:variantInvivoId',
          name: 'variant',
          component: VVariant,
        },
        {
          path: 'catalogue',
          component: VRouterViewPassThrough,
          children: [
            {
              path: '',
              name: 'search-cooperative',
              component: VMicroFrontProvider,
              props: () => ({
                microFrontName: 'catalog',
                routeName: 'catalogue',
                router: getRouter(),
                forwardHostLocation: true,
              }),
            },
            {
              path: 'national',
              name: 'search-national',
              component: VMicroFrontProvider,
              props: () => ({
                microFrontName: 'catalog',
                routeName: 'catalogue/national',
                router: getRouter(),
                forwardHostLocation: true,
              }),
            },
          ],
        },
        {
          // Flat sibling of `catalogue`, not one of its children: VLayoutMain keys its child by
          // `matched[1]`, and a child of the pass-through would share the catalog micro-front's
          // VMicroFrontProvider (and its iframe history), as `commandes/a-valider` would with `commandes`.
          path: 'catalogue/suivi-des-demandes',
          name: 'product-requests',
          component: VMicroFrontProvider,
          props: () => ({
            microFrontName: 'product-management',
            routeName: 'catalogue/suivi-des-demandes',
            router: getRouter(),
          }),
          meta: {
            auth: {
              permissions: PRODUCT_MANAGER_MANAGE_SCOPES,
              type: 'some',
            },
          },
          // Same flag as the menu entry: without this guard, the URL stays reachable for
          // distributors whose entry is hidden. The global guard has loaded the cooperative by now.
          async beforeEnter(_to, _from, next) {
            const isEnabled = await checkFeature.execute({
              id: DynamicFeaturesEnum.PRODUCT_MANAGEMENT,
              type: FeatureType.Dynamic,
              context: { id: String(getStore().getters.cooperativeId) },
            })
            next(isEnabled ? undefined : { name: 'search-cooperative' })
          },
        },
        {
          path: 'quotas',
          name: 'quotas',
          component: VQuotas,
        },
        ...discountRoutes,
        {
          path: 'commandes',
          name: 'orders',
          component: VMicroFrontProvider,
          props: () => ({ microFrontName: 'orders', routeName: 'commandes', router: getRouter() }),
        },
        {
          path: 'commandes/a-valider',
          name: 'orders-to-validate',
          component: VMicroFrontProvider,
          props: () => ({
            microFrontName: 'derogation-validation',
            routeName: 'commandes/a-valider',
            router: getRouter(),
            forwardHostLocation: true,
          }),
          beforeEnter: ordersToValidateFeatureGuard,
        },
        {
          path: 'commandes/a-valider/:orderId',
          name: 'order-to-validate',
          component: VMicroFrontProvider,
          props: (route) => ({
            microFrontName: 'derogation-validation',
            routeName: `commandes/a-valider/${encodeURIComponent(String(route.params.orderId))}`,
            router: getRouter(),
            forwardHostLocation: true,
          }),
          beforeEnter: ordersToValidateFeatureGuard,
        },
        {
          path: 'offers',
          name: 'offers',
          component: VOffers,
        },
        ...logisticRoutes,
        {
          path: 'logistic-config',
          name: 'logistic-config',
          component: VMicroFrontProvider,
          props: () => ({
            microFrontName: 'logistic-config',
            routeName: 'logistic-config',
            router: getRouter(),
          }),
          // Same flag as the menu entry: without this guard, the URL stays reachable on brands
          // where the entry is hidden.
          async beforeEnter(_to, _from, next) {
            const isEnabled = await checkFeature.execute({
              id: EnvFeaturesEnum.LOGISTIC_CONFIG_FEATURE,
              type: FeatureType.Env,
            })
            next(isEnabled ? undefined : { name: LogisticRouteName.LIST })
          },
        },
        {
          path: 'parameters',
          component: VParameters,
          children: [
            {
              path: '',
              name: 'parameters',
              component: VParametersList,
            },
            {
              path: 'adherents',
              name: 'import-members',
              component: CImportMembers,
            },
            {
              path: 'techniciens',
              name: 'import-technical-sales',
              component: CImportTechnicalSales,
            },
            {
              path: 'stores',
              name: 'import-stores',
              component: CImportStores,
            },
            {
              path: 'stock',
              name: 'import-stock',
              component: CImportStock,
            },
            {
              path: 'stock-thresholds',
              name: 'stock-thresholds',
              component: CThresholdsStock,
            },
            {
              path: 'adresses-de-livraison',
              name: 'import-shipping-addresses',
              component: CImportShippingAddresses,
            },
            {
              path: 'codes-adressage-electronique',
              name: 'import-electronic-invoicing-addresses',
              component: CImportElectronicInvoicingAddresses,
            },
            {
              path: 'adresses-de-regroupement',
              name: 'import-grouping-addresses',
              component: VMicroFrontProvider,
              props: () => ({
                microFrontName: 'imports',
                routeName: 'parameters/adresses-de-regroupement',
                router: getRouter(),
              }),
              meta: {
                auth: {
                  permissions: ['shipping-addresses:manage:cooperative'],
                  type: 'some',
                },
              },
            },
            {
              path: 'variantes',
              name: 'import-variants',
              component: CImportVariants,
            },
            {
              path: 'segmentation-variantes',
              name: 'import-variant-segmentations',
              component: CImportVariantSegmentations,
              // La page suit le même feature flip que la tuile Paramètres : sans ce garde,
              // l'URL resterait atteignable alors que la fonctionnalité est coupée.
              async beforeEnter(_to, _from, next) {
                const isEnabled = await checkFeature.execute({
                  id: 'variants-segmentations',
                  type: FeatureType.Dynamic,
                })
                next(isEnabled ? undefined : { name: 'parameters' })
              },
            },
            {
              path: 'products-additional-data',
              name: 'import-products-additional-data',
              component: CImportProductsAdditionalData,
            },
            {
              path: 'virtual-categories',
              component: VRouterViewPassThrough,
              children: [
                {
                  path: '',
                  name: 'virtual-categories',
                  component: VMicroFrontProvider,
                  props: () => ({
                    microFrontName: 'catalog',
                    routeName: 'parameters/virtual-categories',
                    router: getRouter(),
                  }),
                },
                {
                  path: 'create',
                  name: 'virtual-categories-create',
                  component: VMicroFrontProvider,
                  props: () => ({
                    microFrontName: 'catalog',
                    routeName: 'parameters/virtual-categories/create',
                    router: getRouter(),
                  }),
                },
                {
                  path: 'edit/:virtualCategoryId',
                  name: 'virtual-categories-edit-page',
                  component: VMicroFrontProvider,
                  props: (route) => ({
                    microFrontName: 'catalog',
                    routeName: `parameters/virtual-categories/edit/${encodeURIComponent(
                      String(route.params.virtualCategoryId),
                    )}`,
                    router: getRouter(),
                  }),
                },
              ],
            },
            {
              path: 'custom-filters',
              component: VRouterViewPassThrough,
              children: [
                {
                  path: '',
                  name: 'custom-filters',
                  component: VMicroFrontProvider,
                  props: () => ({
                    microFrontName: 'catalog',
                    routeName: 'parameters/custom-filters',
                    router: getRouter(),
                  }),
                },
                {
                  path: 'create',
                  name: 'custom-filters-create',
                  component: VMicroFrontProvider,
                  props: () => ({
                    microFrontName: 'catalog',
                    routeName: 'parameters/custom-filters/create',
                    router: getRouter(),
                  }),
                },
                {
                  // `props: true` is gone: the Vue 2 editor never declared those props, it read
                  // them from the route. The micro-front route carries it instead.
                  path: 'edit/:category/:crop',
                  name: 'custom-filters-edit',
                  component: VMicroFrontProvider,
                  props: (route) => ({
                    microFrontName: 'catalog',
                    routeName: `parameters/custom-filters/edit/${encodeURIComponent(
                      String(route.params.category),
                    )}/${encodeURIComponent(String(route.params.crop))}`,
                    router: getRouter(),
                  }),
                },
              ],
            },
            {
              path: 'offers',
              name: 'import-offers',
              component: CImportOffers,
            },
            {
              path: 'quotas',
              name: 'import-quotas',
              component: CImportQuotas,
            },
            {
              path: 'segments',
              name: 'import-segments',
              component: CImportSegments,
            },
            {
              path: 'contacts',
              name: 'import-contacts',
              component: CImportPartnerContact,
            },
            {
              path: 'store-customer',
              name: 'import-store-customer',
              component: CImportStoreCustomer,
            },
            {
              path: 'users-roles',
              name: 'import-users-roles',
              component: CImportUsersRoles,
            },
            {
              path: 'sales-stores',
              name: 'import-sales-stores',
              component: CImportSalesStores,
            },
            // Articles & News and the home banner live in the `marketing` micro-front (Vue 3).
            // Route names and paths are unchanged so the settings menu, the breadcrumbs and any
            // bookmark keep working; `routeName` mirrors the path inside the iframe.
            {
              path: 'articles',
              name: 'articles',
              component: VMicroFrontProvider,
              props: () => ({
                microFrontName: 'marketing',
                routeName: 'parameters/articles',
                router: getRouter(),
              }),
            },
            {
              path: 'articles/create',
              name: 'article-creation',
              component: VMicroFrontProvider,
              props: () => ({
                microFrontName: 'marketing',
                routeName: 'parameters/articles/create',
                router: getRouter(),
              }),
            },
            {
              path: 'articles/:articleSlug/edit',
              name: 'articles-v2-edit',
              component: VMicroFrontProvider,
              props: (route) => ({
                microFrontName: 'marketing',
                routeName: `parameters/articles/${route.params.articleSlug}/edit`,
                router: getRouter(),
              }),
            },
            {
              path: 'zone-charges',
              name: 'zone-charges',
              component: VZoneCharges,
            },
            {
              path: 'import-reports',
              name: 'import-reports',
              component: VImportReports,
            },
            {
              path: 'import-reports/detail/:importType/:importId',
              name: 'import-report',
              component: VImportReport,
            },
            {
              path: 'details-paiements-cb',
              name: 'details-paiements-cb',
              component: CDetailsPaiementsCb,
            },
            {
              path: 'order-validation',
              name: 'order-validation',
              component: VMicroFrontProvider,
              props: () => ({
                microFrontName: 'payment-validation-configuration',
                routeName: 'parameters/order-validation',
                router: getRouter(),
              }),
            },
            {
              path: 'partner-banner',
              name: 'partner-banner',
              component: VMicroFrontProvider,
              props: () => ({
                microFrontName: 'marketing',
                routeName: 'parameters/partner-banner',
                router: getRouter(),
              }),
            },
            {
              path: 'payment-due-dates',
              name: 'payment-due-dates',
              component: CPaymentDueDatesList,
            },
            ...discountAssociationParametersRoutes,
          ],
        },
        {
          path: 'webhooks',
          name: 'webhooks',
          component: VMicroFrontProvider,
          props: () => ({
            microFrontName: 'webhooks',
            routeName: 'webhooks',
            router: getRouter(),
          }),
        },
        {
          path: 'webhooks/:webhookId',
          name: 'webhook',
          component: VMicroFrontProvider,
          props: (route) => ({
            microFrontName: 'webhooks',
            routeName: `webhooks/${route.params.webhookId}`,
            router: getRouter(),
          }),
        },
        {
          path: 'companies',
          redirect: { name: 'companies-prospects' },
        },
        {
          path: 'companies/prospects',
          name: 'companies-prospects',
          component: VMicroFrontProvider,
          props: () => ({
            microFrontName: 'companies',
            routeName: 'companies/prospects',
            router: getRouter(),
          }),
        },
        {
          path: 'brand/redirections',
          name: 'brand-redirections',
          component: VMicroFrontProvider,
          props: {
            microFrontName: 'brand-redirections',
            routeName: 'brand-redirections',
          },
          meta: {
            auth: {
              permissions: BRAND_MANAGE_SCOPES,
              type: 'some',
            },
          },
        },
      ],
    },
    {
      path: '/',
      component: VLayoutMain,
      children: [
        {
          path: '*',
          name: 'not-found',
          component: VPageNotFound,
        },
      ],
    },
  ]
}
